/**
 * Sprint 23.6 — AgentActionRow.
 *
 * Top-right action strip on the profile hero. Three themed primary buttons +
 * an overflow dots menu absorbing less-common ops (Q4):
 *
 *   • Compose retro  — runs Sprint 21 `compose_retrospective` against the agent's
 *     current top priority. If no priorities exist, the button shows a tooltip
 *     and is disabled (Q22 "give her something to work on" message lives in the
 *     hero's decisions empty state instead).
 *   • Assign priority — adds a new priority to the agent (re-uses the
 *     `useAddPriority` mutation from Sprint 8; ID Cmd-K will open the
 *     PrioritiesPanel inline editor on the new row in a follow-up).
 *   • Edit persona — jumps to the Settings tab (URL hash) where the 5 persona
 *     fields live inline-editable. Sprint 23.5 wizard refine-mode would be a
 *     separate fast-follow; in v1 the Settings tab IS the post-hire persona
 *     surface (matches the "inline pencil edit" pattern Q15 already prescribes).
 *   • Dots menu — Move, Manage access, Demote/Promote, secondary actions that
 *     used to crowd CockpitHeader's primary surface.
 */

import { useState } from "react";
import { useNavigate } from "react-router";
import { showToast } from "../primitives";
import { type ActionMenuItem } from "../shell/ActionMenu";
import { ActionButtonRow, type ActionButtonRowAction } from "../shell/ActionButtonRow";
import { MoveToModal } from "../scope/MoveToModal";
import { ArchiveModal } from "../scope/ArchiveModal";
import { ManageAccessModal } from "../scope/ManageAccessModal";
import { DemoteModal } from "../cockpit/DemoteModal";
import { PromoteModal } from "../cockpit/PromoteModal";
import { PlanWithAgentModal } from "../scope/PlanWithAgentModal";
import { useCreateTask, useAgentTaskQueue } from "../../hooks/use-agent-tasks-26";
import { useThemedLabels } from "../../hooks/use-themed-labels";
import { useIsOwner } from "../../hooks/use-is-owner";
import { mcpClient } from "../../../lib/mcp-client";
import type { LibraryNodeDetail } from "../../../lib/types";

export interface AgentActionRowProps {
  node: LibraryNodeDetail;
  /** Owner gate — disables Manage access / Archive in the dots menu. */
  canEdit: boolean;
}

export function AgentActionRow({ node, canEdit }: AgentActionRowProps) {
  const navigate = useNavigate();
  const labels = useThemedLabels();
  const createTask = useCreateTask();
  const queue = useAgentTaskQueue(node.id);
  const isOwner = useIsOwner();
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [demoteOpen, setDemoteOpen] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [composing, setComposing] = useState(false);

  const isRoot = !!node.is_root;
  const isFolder = node.type === "folder";
  const topTask = (queue.data ?? []).find((t) => t.status === "queued") ?? null;

  async function composeRetro() {
    if (!topTask || composing) return;
    setComposing(true);
    try {
      const result = await mcpClient.toolsCall<{
        retrospective_page_id?: string;
        retrospective_page_name?: string;
        thin?: boolean;
      }>("compose_retrospective", { task_id: topTask.id });
      if (result.isError) {
        const msg =
          (result.structuredContent as { message?: string } | undefined)?.message ??
          "Couldn't compose retro";
        showToast(msg, "error", true);
        return;
      }
      const sc = result.structuredContent ?? {};
      if (sc.thin) {
        showToast(
          `${node.name}: composed a thin retro — not much evidence yet.`,
        );
      } else {
        showToast(
          `${node.name}: composed retrospective.`,
        );
      }
      if (sc.retrospective_page_id) {
        // Deep-link to the new Page (under Pages tab).
        navigate(`/pages/${sc.retrospective_page_id}`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "compose failed";
      showToast(`Couldn't compose retro: ${msg}`, "error", true);
    } finally {
      setComposing(false);
    }
  }

  function handleAssign() {
    createTask.mutate(
      { agent_id: node.id, title: "New task", status: "queued" },
      {
        onSuccess: () => {
          // Jump to the Tasks tab so the new row is in the user's view.
          window.location.hash = "#tasks";
          showToast(`Added a task to ${node.name}.`);
        },
        onError: (err: unknown) =>
          showToast(
            `Couldn't add task: ${err instanceof Error ? err.message : "error"}`,
            "error",
            true,
          ),
      },
    );
  }

  function handleEditPersona() {
    // Sprint 23.6 v1: the Settings tab's Persona section IS the post-hire
    // persona surface (5 inline-edit fields + sparkle regen). A wizard
    // refine-mode would change Sprint 23.5's hire flow shape — kept as a
    // fast-follow per the spec's risk table.
    window.location.hash = "#settings";
  }

  const dots: ActionMenuItem[] = [];
  if (!isRoot && !isFolder) {
    dots.push({ label: "Plan with this agent", icon: "wand", onClick: () => setPlanOpen(true) });
    dots.push({ label: "Move to…", icon: "arrows-move", onClick: () => setMoveOpen(true) });
    dots.push({ label: "Demote to folder", icon: "folder", onClick: () => setDemoteOpen(true) });
  }
  if (isFolder && !isRoot) {
    dots.push({ label: "Promote to agent", icon: "robot", onClick: () => setPromoteOpen(true) });
    dots.push({ label: "Move to…", icon: "arrows-move", onClick: () => setMoveOpen(true) });
  }
  if (isOwner && !isRoot) {
    dots.push({ label: "Manage access", icon: "lock", onClick: () => setAccessOpen(true) });
  }
  if (canEdit && !isRoot) {
    dots.push({
      label: isFolder ? "Archive folder" : "Archive agent",
      icon: "archive",
      variant: "danger",
      onClick: () => setArchiveOpen(true),
    });
  }

  const actions: ActionButtonRowAction[] = [];
  if (!isFolder && !isRoot) {
    actions.push({
      label: composing ? "Composing…" : labels.composeRetroAction,
      icon: "notebook",
      onClick: composeRetro,
      disabled: !topTask || composing,
      title: topTask
        ? `Compose a retrospective for "${topTask.title}"`
        : "No current task — add one first",
    });
    actions.push({
      label: "Add task",
      icon: "target",
      onClick: handleAssign,
      disabled: createTask.isPending,
      title: `Add a task for ${node.name}`,
    });
    actions.push({
      label: labels.editPersonaAction,
      icon: "pencil",
      onClick: handleEditPersona,
      title: `${labels.editPersonaAction} for ${node.name}`,
    });
  }

  return (
    <div>
      <ActionButtonRow actions={actions} menuItems={dots} />

      <ArchiveModal
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        nodes={[node]}
        onArchived={() => {
          const parent = node._breadcrumb?.length
            ? node._breadcrumb[node._breadcrumb.length - 1]
            : null;
          navigate(parent ? `/agents/${parent.id}` : "/workforce");
        }}
      />
      <MoveToModal
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        sources={[node]}
        onMoved={() => navigate(`/agents/${node.id}`)}
      />
      <ManageAccessModal
        open={accessOpen}
        onClose={() => setAccessOpen(false)}
        name={node.name}
        nodeId={node.id}
        currentAllowedUsers={node.access_scope?.allowed_users ?? null}
      />
      <DemoteModal open={demoteOpen} onOpenChange={setDemoteOpen} node={node} />
      <PromoteModal open={promoteOpen} onOpenChange={setPromoteOpen} node={node} />
      {!isFolder && (
        <PlanWithAgentModal
          open={planOpen}
          onClose={() => setPlanOpen(false)}
          agentId={node.id}
          agentName={node.name}
        />
      )}
    </div>
  );
}
